import { useState, useEffect } from "react";
import { Button, DialogActions, DialogContent, TextField, CircularProgress, Typography } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import axios from "axios";
import { ReactComponent as AvatarImage } from "../../../assets/directtv_avatar.svg";
import { DealerAvatar } from "../../atoms";
import * as S from "./styles";
import { colors } from "@/theme";
import { AdminData } from "@/interfaces";

export type LoginAsAdminDialogProps = {
  open?: boolean;
  onClose?: (isAdmin?: boolean, adminData?: AdminData) => void;
};

const textFieldSx = {
  "& .MuiInputBase-root": {
    backgroundColor: colors.fieldBg,
    "font-family": "PF DIN Text Pro",
    "font-size": "14px",
    "font-style": "normal",
    "font-weight": "400",
    "line-height": "16px",
  },
};

const STORAGE_TOKEN_KEY = "admin_token";

// Utility function to clear stored admin credentials
export const clearAdminCredentials = () => {
  localStorage.removeItem(STORAGE_TOKEN_KEY);
  // Also clear any old email/password from previous implementation
  localStorage.removeItem("admin_email");
  localStorage.removeItem("admin_password");
};

// Utility function to get stored token
export const getStoredToken = () => {
  return localStorage.getItem(STORAGE_TOKEN_KEY);
};

// Utility function to set token
export const setStoredToken = (token: string) => {
  localStorage.setItem(STORAGE_TOKEN_KEY, token);
};

const LoginAsAdminDialog: React.FC<LoginAsAdminDialogProps> = ({
  open = false,
  onClose,
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isEmailInvalid, setIsEmailInvalid] = useState(false);
  const [isPasswordInvalid, setIsPasswordInvalid] = useState(false);
  const invalidEmailMessage = "Invalid email";
  const invalidPasswordMessage = "Invalid password";

  // Auto-login on mount if token exists
  useEffect(() => {
    if (!open) return;
    
    // Clear any old email/password from previous implementation
    localStorage.removeItem("admin_email");
    localStorage.removeItem("admin_password");
    
    const storedToken = localStorage.getItem(STORAGE_TOKEN_KEY);
    
    if (storedToken) {
      // Validate token by calling profile endpoint
      validateToken(storedToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const validateToken = async (token: string) => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_REST_BASE_URL}/User/profile`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // If we get a successful response, the token is valid
      if (response.data && response.data.userId) {
        const adminData: AdminData = {
          userId: response.data.userId,
          email: response.data.email,
          permissions: response.data.permission,
          token: token,
        };
        handleClose(true, adminData);
      } else {
        // Token is invalid, clear it
        clearAdminCredentials();
      }
    } catch (error) {
      console.error("Token validation error:", error);
      // Token is invalid or expired, clear it
      clearAdminCredentials();
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);

    if (isEmailInvalid) {
      setIsEmailInvalid(false);
    }
    if (errorMessage) {
      setErrorMessage("");
    }
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);

    if (isPasswordInvalid) {
      setIsPasswordInvalid(false);
    }
    if (errorMessage) {
      setErrorMessage("");
    }
  };

  const handleClose = (isAdmin?: boolean, adminData?: AdminData) => {
    setEmail("");
    setPassword("");
    setIsEmailInvalid(false);
    setIsPasswordInvalid(false);
    setErrorMessage("");
    onClose?.(isAdmin, adminData);
  };

  const handleLogin = async (emailValue: string, passwordValue: string) => {
    setIsLoading(true);
    setErrorMessage("");
    setIsEmailInvalid(false);
    setIsPasswordInvalid(false);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_REST_BASE_URL}/User/login`,
        {
          email: emailValue,
          password: passwordValue,
        }
      );

      if (response.data.success && response.data.token) {
        // Store token for auto-login
        localStorage.setItem(STORAGE_TOKEN_KEY, response.data.token);

        // Create admin data object from response
        const adminData: AdminData = {
          userId: response.data.userId,
          email: response.data.email,
          permissions: response.data.permission,
          token: response.data.token,
        };

        handleClose(true, adminData);
      } else {
        setErrorMessage(response.data.message || "Login failed");
        setIsEmailInvalid(true);
        setIsPasswordInvalid(true);
      }
    } catch (error: any) {
      console.error("Login error:", error);
      setErrorMessage(
        error.response?.data?.message || "Login failed. Please try again."
      );
      setIsEmailInvalid(true);
      setIsPasswordInvalid(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginClick = () => {
    if (!email || !password) {
      if (!email) setIsEmailInvalid(true);
      if (!password) setIsPasswordInvalid(true);
      return;
    }
    
    handleLogin(email, password);
  };

  return (
    <Dialog open={open} fullWidth maxWidth="xs" onClose={() => handleClose()}>
      <DialogContent>
        <S.LoginAsAdminDialogContentContainer>
          <S.LoginAsAdminDialogContentAvatarContainer>
            <DealerAvatar children={<AvatarImage />} width={96} height={96} />
          </S.LoginAsAdminDialogContentAvatarContainer>
          <S.LoginAsAdminDialogContentTitle>
            LOG IN AS ADMIN
          </S.LoginAsAdminDialogContentTitle>
          <TextField
            onChange={(e) => handleEmailChange(e.target.value)}
            value={email}
            autoFocus
            id="email"
            label="Email"
            type="text"
            fullWidth
            error={isEmailInvalid}
            helperText={isEmailInvalid && !errorMessage ? invalidEmailMessage : undefined}
            placeholder="Enter admin email"
            sx={textFieldSx}
            disabled={isLoading}
          />
          <TextField
            onChange={(e) => handlePasswordChange(e.target.value)}
            value={password}
            id="password"
            label="Password"
            type="password"
            fullWidth
            error={isPasswordInvalid}
            helperText={isPasswordInvalid && !errorMessage ? invalidPasswordMessage : undefined}
            placeholder="Enter admin password"
            sx={textFieldSx}
            disabled={isLoading}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !isLoading) {
                handleLoginClick();
              }
            }}
          />
          {errorMessage && (
            <Typography
              sx={{ 
                color: "#d32f2f", 
                fontSize: "14px",
                marginTop: "8px",
                textAlign: "center",
                fontFamily: "PF DIN Text Pro",
              }}
            >
              {errorMessage}
            </Typography>
          )}
        </S.LoginAsAdminDialogContentContainer>
      </DialogContent>
      <DialogActions
        sx={{
          "&.MuiDialogActions-root": {
            "justify-content": "center",
            padding: "24px",
          },
        }}
      >
        <Button
          onClick={() => handleLoginClick()}
          disabled={isLoading}
          sx={{
            width: "100%",
            color: colors.white,
            background: colors.ozone,
            "border-radius": "12px",
            padding: "12px",
            "font-family": "PF DIN Text Pro",
            "font-size": "12px",
            "font-style": "normal",
            "font-weight": "700",
            "line-height": "16px",
            "text-transform": "uppercase",
            "&:hover": {
              color: colors.white,
              background: colors.ozone,
            },
            "&.Mui-disabled": {
              color: colors.white,
              background: colors.neutralGrey500,
            },
          }}
        >
          {isLoading ? (
            <CircularProgress size={20} sx={{ color: colors.white }} />
          ) : (
            "SIGN IN"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoginAsAdminDialog;
